import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  FileText,
  Loader2,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  ChevronRight,
  X,
  Info,
  BookOpen,
  Search,
} from "lucide-react";
import {
  useGetAvailableExamCallsQuery,
  useApplyToExamCallMutation,
  useGetMyExamCallApplicationsQuery,
  useInitiateExamPaymentMutation,
} from "../../api/examCallsApi";
import { submitOnePayForm } from "../../../logbook/utils/onepay";
import OutstandingDuesModal from "../../components/OutstandingDuesModal";
import CpdShortfallModal from "../../components/CpdShortfallModal";

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(iso);
  }
}

function daysLeft(iso?: string | null): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / 86_400_000);
}

function safeMsg(err: any) {
  return (
    err?.data?.message ||
    err?.data?.error ||
    err?.message ||
    "Something went wrong"
  );
}

/* ------------------------------------------------------------------ */
/* Apply Modal                                                          */
/* ------------------------------------------------------------------ */
type ApplyModalProps = {
  call: any;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  loading: boolean;
  error?: string | null;
};

function ApplyModal({
  call,
  onConfirm,
  onClose,
  loading,
  error,
}: ApplyModalProps) {
  const requirements: string[] = Array.isArray(call.requirements)
    ? call.requirements
    : [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Confirm Application</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Call summary */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-2">
            <p className="font-semibold text-gray-900 text-sm">
              {call.exam?.title ?? "—"}
            </p>
            <p className="text-xs text-indigo-700">
              {call.diet?.title ?? "—"}
              {call.title ? ` · ${call.title}` : ""}
            </p>
            <div className="flex items-center gap-4 text-xs text-indigo-600 pt-1">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> Closes {fmtDate(call.closes_at)}
              </span>
              <span className="flex items-center gap-1">
                {call.application_fee != null
                  ? `₦${Number(call.application_fee).toLocaleString()}`
                  : "Free"}
              </span>
            </div>
          </div>

          {/* Instructions */}
          {call.instructions && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-800">
              <p className="font-semibold mb-1 flex items-center gap-1.5">
                <Info className="w-4 h-4" /> Instructions
              </p>
              <p className="text-xs">{call.instructions}</p>
            </div>
          )}

          {/* Requirements */}
          {requirements.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Requirements
              </p>
              <ul className="space-y-1.5">
                {requirements.map((r: string, i: number) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-xs text-gray-700"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <p className="text-xs text-gray-500">
            By clicking <strong>Apply Now</strong>, you confirm that you meet
            the eligibility requirements for this exam call. Your application
            will be created as a <strong>draft</strong> — you must submit it
            before the closing date.
          </p>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 border rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Call Card                                                            */
/* ------------------------------------------------------------------ */
// Each exam gets its own colour so the different exams are easy to tell apart.
type ExamPalette = { bar: string; badge: string };
const EXAM_PALETTE: ExamPalette[] = [
  { bar: "bg-indigo-500", badge: "bg-indigo-50 text-indigo-700" },
  { bar: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700" },
  { bar: "bg-amber-500", badge: "bg-amber-50 text-amber-700" },
  { bar: "bg-rose-500", badge: "bg-rose-50 text-rose-700" },
  { bar: "bg-sky-500", badge: "bg-sky-50 text-sky-700" },
  { bar: "bg-violet-500", badge: "bg-violet-50 text-violet-700" },
  { bar: "bg-teal-500", badge: "bg-teal-50 text-teal-700" },
  { bar: "bg-fuchsia-500", badge: "bg-fuchsia-50 text-fuchsia-700" },
];
const DEFAULT_PALETTE: ExamPalette = EXAM_PALETTE[0];
const examKey = (call: any) => String(call?.exam?.code ?? call?.exam?.id ?? call?.exam_id ?? "");

function CallCard({
  call,
  alreadyApplied,
  onApply,
  palette = DEFAULT_PALETTE,
}: {
  call: any;
  alreadyApplied: boolean;
  onApply: (call: any) => void;
  palette?: ExamPalette;
}) {
  const days = daysLeft(call.closes_at);
  const urgency = days !== null && days <= 3 && days >= 0;

  return (
    <div
      className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-all hover:shadow-md ${
        alreadyApplied ? "opacity-80" : ""
      }`}
    >
      {/* Top accent — colour-coded per exam */}
      <div
        className={`h-1 ${alreadyApplied ? "bg-green-400" : palette.bar}`}
      />

      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {call.exam?.code && (
                <span className={`px-2 py-0.5 ${palette.badge} text-[10px] font-bold rounded-full uppercase`}>
                  {call.exam.code}
                </span>
              )}
              {alreadyApplied && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full uppercase flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Applied
                </span>
              )}
              {urgency && !alreadyApplied && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full uppercase animate-pulse">
                  Closing Soon
                </span>
              )}
            </div>
            <h3 className="font-semibold text-gray-900 text-sm leading-snug">
              {call.exam?.title ?? "Exam Call"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {call.diet?.title ?? "—"}
              {call.title ? ` · ${call.title}` : ""}
            </p>
          </div>
        </div>

        {/* Meta row */}
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <div>
              <p className="text-[10px] text-gray-400">Opens</p>
              <p className="font-medium">{fmtDate(call.opens_at)}</p>
            </div>
          </div>
          <div
            className={`flex items-center gap-1.5 rounded-lg px-2.5 py-2 ${
              urgency ? "bg-red-50" : "bg-gray-50"
            }`}
          >
            <Clock
              className={`w-3.5 h-3.5 ${
                urgency ? "text-red-400" : "text-gray-400"
              }`}
            />
            <div>
              <p
                className={`text-[10px] ${
                  urgency ? "text-red-400" : "text-gray-400"
                }`}
              >
                Closes
              </p>
              <p className={`font-medium ${urgency ? "text-red-700" : ""}`}>
                {fmtDate(call.closes_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Fee + days */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className="flex items-center gap-1">
            {call.application_fee != null ? (
              <span className="font-semibold text-gray-700">
                ₦{Number(call.application_fee).toLocaleString()}
              </span>
            ) : (
              <span className="text-green-600 font-semibold">Free</span>
            )}
          </span>
          {days !== null && days >= 0 && (
            <span className={urgency ? "text-red-600 font-semibold" : ""}>
              {days === 0
                ? "Closes today"
                : `${days} day${days !== 1 ? "s" : ""} left`}
            </span>
          )}
          {days !== null && days < 0 && (
            <span className="text-gray-400">Closed</span>
          )}
        </div>

        {/* Instructions preview */}
        {call.instructions && (
          <p className="text-[11px] text-gray-500 border-t pt-3 line-clamp-2">
            {call.instructions}
          </p>
        )}

        {/* CTA */}
        <div className="pt-1">
          {alreadyApplied ? (
            <Link
              to="/exams/calls/applications"
              className="w-full py-2.5 rounded-lg bg-green-50 text-green-700 border border-green-200 text-sm font-medium hover:bg-green-100 transition-colors flex items-center justify-center gap-2"
            >
              <FileText className="w-4 h-4" /> View My Application
            </Link>
          ) : (
            <button
              onClick={() => onApply(call)}
              className="w-full py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              Apply Now
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Page                                                            */
/* ------------------------------------------------------------------ */
export default function ExamCallsPage() {
  const [applyTarget, setApplyTarget] = useState<any | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [q, setQ] = useState("");

  // Outstanding-dues modal — shown (instead of blocking) when eligibility fails
  // because the member owes dues.
  const [duesOpen, setDuesOpen] = useState(false);
  const [duesBalanceKobo, setDuesBalanceKobo] = useState<number | null>(null);
  const [duesReason, setDuesReason] = useState<string | null>(null);

  // CPD-shortfall modal — the same idea for the other blocker a member can
  // clear on the spot. The call being applied for is held separately: the
  // apply modal closes behind it, and the retry has to know what to re-apply
  // for.
  const [cpdOpen, setCpdOpen] = useState(false);
  const [cpdInfo, setCpdInfo] = useState<any>(null);
  const [cpdReason, setCpdReason] = useState<string | null>(null);
  const [cpdTarget, setCpdTarget] = useState<any | null>(null);
  // Whether the server offered the declaration on this refusal. Defaulted to
  // true so the way out survives an API deployed before it existed.
  const [cpdCanAttest, setCpdCanAttest] = useState(true);

  const {
    data: calls = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAvailableExamCallsQuery();
  const { data: myApps = [], refetch: refetchMyApps } = useGetMyExamCallApplicationsQuery();
  const [applyMutation, { isLoading: applying }] = useApplyToExamCallMutation();
  const [initiatePayment, { isLoading: initiating }] = useInitiateExamPaymentMutation();

  const appliedCallIds = useMemo(
    () => new Set(myApps.map((a: any) => a.exam_call_id)),
    [myApps],
  );

  // Give each distinct exam its own colour (stable across the whole list,
  // regardless of the search filter).
  const examColorMap = useMemo(() => {
    const keys = Array.from(new Set((calls as any[]).map(examKey).filter(Boolean))).sort();
    const map: Record<string, ExamPalette> = {};
    keys.forEach((k, i) => {
      map[k] = EXAM_PALETTE[i % EXAM_PALETTE.length];
    });
    return map;
  }, [calls]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return calls;
    return calls.filter((c: any) => {
      const blob = `${c.exam?.title ?? ""} ${c.exam?.code ?? ""} ${
        c.diet?.title ?? ""
      } ${c.title ?? ""}`.toLowerCase();
      return blob.includes(s);
    });
  }, [calls, q]);

  // If a response (2xx body or a thrown error's .data) is the outstanding-dues
  // blocker, open the pay-dues modal and report that it was handled.
  const openDuesIfOwed = (data: any): boolean => {
    const d = data?.code ? data : data?.data;
    if (d?.code === "NOT_FINANCIALLY_UP_TO_DATE") {
      setDuesBalanceKobo(d.outstanding_balance_kobo ?? null);
      setDuesReason(d.message ?? null);
      setApplyTarget(null);
      setApplyError(null);
      setDuesOpen(true);
      return true;
    }
    return false;
  };

  /**
   * The CPD bar, which the server checks BEFORE taking any money. A member who
   * is short is not sent away: the modal lets them claim the CPD they have
   * already earned, and claimed points count immediately — so "claim, then pay"
   * happens in one sitting.
   *
   * Where a claim is not the answer — the points are earned but the paperwork
   * is elsewhere — they may instead declare that they hold them, which the
   * server records on the application it then lets through.
   *
   * Unlike the dues blocker this keeps the modal open on a repeat refusal and
   * refreshes the figures instead, because a claim can leave them short.
   */
  const openCpdIfShort = (data: any, target?: any): boolean => {
    const d = data?.code ? data : data?.data;
    if (d?.code === "CPD_SHORT") {
      setCpdInfo(d.cpd ?? null);
      setCpdReason(d.message ?? null);
      setCpdCanAttest(d.can_attest ?? true);
      if (target) setCpdTarget(target);
      setApplyTarget(null);
      setApplyError(null);
      setCpdOpen(true);
      return true;
    }
    return false;
  };

  const runApply = async (target: any, cpdAttested = false) => {
    if (!target) return;
    setApplyError(null);
    try {
      // 1) Apply — the server checks eligibility (logbook stage + outstanding
      //    papers + financial standing + this exam's CPD/age bars) and creates
      //    a draft if the member qualifies. `cpd_attested` carries the
      //    declaration made in the shortfall modal, and is recorded on the
      //    application it lets through.
      const applied: any = await applyMutation({
        callId: target.id,
        ...(cpdAttested ? { cpd_attested: true } : {}),
      }).unwrap();

      // Blocked by outstanding dues? Offer to pay (modal) instead of dead-ending.
      // (Some backends return this blocker with a 2xx status.)
      if (openDuesIfOwed(applied)) return;
      if (openCpdIfShort(applied, target)) return;

      let appId =
        applied?.data?.id ?? applied?.id ?? applied?.application_id ?? applied?.data?.application_id;

      // Fallback: if the response shape didn't carry an id, find the draft we just
      // created from the (freshly refetched) applications list.
      if (!appId) {
        try {
          const fresh: any = await refetchMyApps().unwrap();
          const list: any[] = Array.isArray(fresh) ? fresh : fresh?.data ?? [];
          appId = list
            .filter((a: any) => a.exam_call_id === target.id)
            .sort((a: any, b: any) => String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")))[0]?.id;
        } catch {
          /* ignore — handled below */
        }
      }
      if (!appId) {
        // The server answered with a reason but no application — most often an
        // eligibility blocker returned with a 2xx status (stage / papers / dues).
        // Surface that reason; only fall back to the generic notice if there's none.
        throw new Error(
          applied?.message ||
            applied?.data?.message ||
            "Online exam payment isn't available on the server yet. Please try again later or contact the secretariat.",
        );
      }

      // 2) Eligible → go straight to payment: initiate OnePay and redirect.
      // Carried through to payment as well: apply files the declaration, but a
      // draft that already existed was never filed against one.
      const pay: any = await initiatePayment(
        cpdAttested ? { id: appId, cpd_attested: true } : appId,
      ).unwrap();
      if (pay?.already_paid) {
        setApplyTarget(null);
        setCpdOpen(false);
        setSuccessMsg("This application is already paid. Select your center to finish.");
        return;
      }
      if (!pay?.payment_url) {
        throw new Error(
          pay?.message ||
            pay?.data?.message ||
            "Online exam payment isn't available on the server yet. Please try again later or contact the secretariat.",
        );
      }
      submitOnePayForm(pay); // leaves the app for the OnePay gateway
    } catch (err) {
      // 422 carries the eligibility reason (stage / papers / dues / CPD).
      if (openDuesIfOwed((err as any)?.data)) return;
      if (openCpdIfShort((err as any)?.data, target)) return;
      // A retry from the CPD modal has no apply modal to show the error in.
      if (cpdOpen) setCpdReason(safeMsg(err));
      setApplyError(safeMsg(err));
    }
  };

  const handleApply = () => runApply(applyTarget);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">
                Available Exam Calls
              </h1>
              <p className="text-sm text-gray-500">
                View open exam application windows and submit your application
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            <Link
              to="/exams/calls/applications"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              <FileText className="w-4 h-4" /> My Applications
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 relative max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by exam or diet…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>
      </div>

      {/* Success banner */}
      {successMsg && (
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold mb-0.5">Application Created!</p>
            <p>{successMsg}</p>
          </div>
          <button onClick={() => setSuccessMsg(null)}>
            <X className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="py-20 flex flex-col items-center gap-2 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-sm">Loading available exam calls…</p>
        </div>
      ) : error ? (
        <div className="py-16 text-center bg-white border rounded-xl shadow-sm space-y-3">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
          <p className="text-sm text-red-500">
            Failed to load exam calls. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center bg-white border rounded-xl shadow-sm space-y-2">
          <Calendar className="w-10 h-10 mx-auto text-gray-200" />
          <p className="text-sm text-gray-500">
            {calls.length === 0
              ? "No exam calls are currently open."
              : "No calls match your search."}
          </p>
          {calls.length === 0 && (
            <p className="text-xs text-gray-400 max-w-sm mx-auto">
              Check back later or contact the NIQS secretariat for information
              about upcoming examination diets.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((call: any) => (
            <CallCard
              key={call.id}
              call={call}
              alreadyApplied={appliedCallIds.has(call.id)}
              palette={examColorMap[examKey(call)] ?? DEFAULT_PALETTE}
              onApply={(c) => {
                setApplyTarget(c);
                setApplyError(null);
              }}
            />
          ))}
        </div>
      )}

      {/* Info footer */}
      {!isLoading && !error && calls.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
          <p className="font-semibold mb-1">How it works</p>
          <ol className="space-y-1 list-decimal list-inside text-xs text-blue-600">
            <li>
              Click <strong>Apply Now</strong> on any open exam call.
            </li>
            <li>
              Your application is created as a <strong>draft</strong>.
            </li>
            <li>
              Go to <strong>My Applications</strong> and click{" "}
              <strong>Submit</strong> before the closing date.
            </li>
          </ol>
        </div>
      )}

      {/* Apply modal */}
      {applyTarget && (
        <ApplyModal
          call={applyTarget}
          onConfirm={handleApply}
          onClose={() => {
            setApplyTarget(null);
            setApplyError(null);
          }}
          loading={applying || initiating}
          error={applyError}
        />
      )}

      <OutstandingDuesModal
        open={duesOpen}
        onClose={() => setDuesOpen(false)}
        balanceKobo={duesBalanceKobo}
        reason={duesReason}
      />

      <CpdShortfallModal
        open={cpdOpen}
        onClose={() => setCpdOpen(false)}
        cpd={cpdInfo}
        reason={cpdReason}
        examTitle={cpdTarget?.exam?.title}
        canAttest={cpdCanAttest}
        onRetry={(attested) => runApply(cpdTarget, attested)}
        retrying={applying || initiating}
      />
    </div>
  );
}
